/* eslint-disable max-classes-per-file */
/**
 * Keep ValidationError subclasses to a minimum--mainly useful when wanting to render JSX
 */
import React from 'react'
import { i18n } from '@fs/zion-locale'
import { Link } from '@fs/zion-router'
import { Trans } from 'react-i18next/icu.macro'
import { tokenInvalidString } from '@fs/identity.js'
import { FieldNames } from './constants'
import logError from './services/logError'
import { getProviderTextFromUrl } from './hooks/useProviderText'
import useSiteLink from './hooks/useSiteLink'

export class ValidationError extends Error {
  constructor(fieldName, ...params) {
    super(...params)
    this.fieldName = fieldName
  }

  render() {
    return this.message
  }
}

export class MultiValidationError extends Error {
  constructor(validationErrors = []) {
    super('Multiple validation errors')
    this.errors = validationErrors.filter((x) => {
      if (x instanceof ValidationError) {
        return true
      }
      logError(`MultiValidationError not a ValidationError: ${x}`)
      return false
    })
  }
}

const SignUpLink = ({ children, pathname, username }) => (
  // has to have children so it will work with i18n locales sync script
  // use wayfinding otherwise it may be the same color as the validation error messages
  <Link external to={useSiteLink({ pathname: `/identity/signup/${pathname}`, username })} wayfinding>
    {children}
  </Link>
)

export class DeletedAccountError extends ValidationError {
  constructor(fieldName = FieldNames.USERNAME) {
    super(fieldName, i18n.t('deleted-account.validation', 'Your account has been deleted.'))
  }
}
export class LockedAccountError extends ValidationError {
  constructor(fieldName = FieldNames.USERNAME) {
    super(
      fieldName,
      i18n.t('locked-account.validation', 'Your account has been locked temporarily. Please try again later.')
    )
  }
}

export class AlreadyLinkedError extends ValidationError {
  constructor() {
    const providerText = getProviderTextFromUrl()
    super(
      FieldNames.USERNAME,
      i18n.t('already-linked.validation', 'This account is already linked to {providerText}.', { providerText })
    )
  }
}

export class DisabledAccountError extends ValidationError {
  constructor(fieldName = FieldNames.USERNAME) {
    super(
      fieldName,
      i18n.t(
        'disabled-account.validation',
        'This account has been suspended. You can contact Support for more information.'
      )
    )
  }
}

const incorrectUsernameOrPasswordString = () =>
  i18n.t('incorrect-username-password.validation', 'The username or password was incorrect.')

export class MaybeIncorrectUsernameError extends ValidationError {
  constructor() {
    super(FieldNames.USERNAME, incorrectUsernameOrPasswordString())
  }
}

export class MaybeIncorrectPasswordError extends ValidationError {
  constructor() {
    super(FieldNames.CURRENT_PASSWORD, incorrectUsernameOrPasswordString())
  }
}

export class NeedsParentalConsentSmsError extends ValidationError {
  constructor(username) {
    super(
      FieldNames.USERNAME,
      'Your parent has not given permission to activate your account. Your parent must enter the activation code sent to their mobile phone to continue.'
    )
    this.username = username
  }

  render() {
    return (
      <Trans
        i18nKey="parental-sms-consent-missing.validation"
        defaults="Your parent has not given permission to activate your account. Your parent must enter the activation code sent to their mobile phone to continue. <ActivateParentalConsentSmsLink>Enter Activation Code</ActivateParentalConsentSmsLink>"
        components={{
          ActivateParentalConsentSmsLink: <SignUpLink username={this.username} pathname="parent-sms" />,
        }}
      />
    )
  }
}

export class NeedsParentalConsentEmailError extends ValidationError {
  constructor(username) {
    super(
      FieldNames.USERNAME,
      'Your parent has not given permission to activate your account. Your parent must click the activation link that we have sent to their email address to continue.'
    )
    this.username = username
  }

  render() {
    return (
      <Trans
        i18nKey="parental-email-consent-missing.validation"
        defaults="Your parent has not given permission to activate your account. Your parent must click the activation link that we have sent to their email address to continue. <ResendParentalConsentEmailLink>Resend Email</ResendParentalConsentEmailLink>"
        components={{
          ResendParentalConsentEmailLink: <SignUpLink username={this.username} pathname="parent-email" />,
        }}
      />
    )
  }
}

export class UnverifiedEmailError extends ValidationError {
  constructor(username) {
    super(
      FieldNames.USERNAME,
      'Your account has not been activated. You must click the activation link that we have sent to your email address.'
    )
    this.username = username
  }

  render() {
    return (
      <Trans
        i18nKey="unverified-email.validation"
        defaults="Your account has not been activated. You must click the activation link that we have sent to your email address. <ResendEmailLink>Resend Email</ResendEmailLink>"
        components={{
          ResendEmailLink: <SignUpLink username={this.username} pathname="validate-email" />,
        }}
      />
    )
  }
}

export class UnverifiedSmsError extends ValidationError {
  constructor(username) {
    super(
      FieldNames.USERNAME,
      'Your account has not been activated. You must enter the activation code sent to your mobile phone to continue.'
    )
    this.username = username
  }

  render() {
    return (
      <Trans
        i18nKey="unverified-sms.validation"
        defaults="Your account has not been activated. You must enter the activation code sent to your mobile phone to continue. <ActivateMyAccountLink>Enter Activation Code</ActivateMyAccountLink>"
        components={{
          ActivateMyAccountLink: <SignUpLink username={this.username} pathname="validate-sms" />,
        }}
      />
    )
  }
}

export class InvalidTokenError extends ValidationError {
  constructor() {
    super(FieldNames.VERIFICATION_CODE, tokenInvalidString())
  }
}
